import { LegalPage } from "../../components/LegalPage";

/**
 * S47 — Trust hub disclosure for the in-app Help drawer + page-context AI.
 */
export function HelpAiMethodology() {
  return (
    <LegalPage
      title="In-app Help AI methodology"
      eyebrow="Trust center"
      description="How the WeConsult Rx in-app Help drawer and page-context AI assistant work, what they do not do, and how queries are audited without storing PHI."
      path="/trust/help-ai-methodology"
      effective="2026-05-31"
    >
      <h2>What the in-app Help drawer does</h2>
      <p>
        Every page in the WeConsult Rx app exposes a Help drawer (keyboard
        shortcut <code>?</code>). The drawer surfaces curated Knowledge Base
        (KB) articles authored by our team and, when no article matches, a
        page-context AI assistant that synthesizes a short answer with
        citations to the KB.
      </p>

      <h2>Page-context awareness</h2>
      <p>
        The drawer reads the URL of the page the user is on (for example{" "}
        <code>/triage</code> or <code>/residents/:id</code>) and biases the
        article list and AI response toward content tagged with that route.
        The route path is part of the question payload sent to the backend.
      </p>

      <h2>What it explicitly does not do</h2>
      <ul>
        <li>
          The Help AI does not generate clinical advice. Drug interaction,
          dosing, monitoring, or other clinical questions are redirected to
          the dedicated AI Assistant surface inside the app.
        </li>
        <li>
          The Help AI does not generate legal or HIPAA advice. State board,
          HIPAA, Joint Commission, ASHP, and CMS questions are redirected to
          the Regulatory Assistant, which carries source citations.
        </li>
        <li>
          The Help AI does not invent features. The system prompt instructs
          the model to recommend the Universal Search or the Regulatory
          Assistant when no KB article matches.
        </li>
      </ul>

      <h2>Educational reference disclaimer</h2>
      <p>
        Every AI response carries an "Educational reference only" disclaimer.
        Users are expected to confirm any cited content with the linked KB
        article or the underlying source document before relying on it in a
        recommendation, report, or compliance artifact.
      </p>

      <h2>Privacy and audit</h2>
      <ul>
        <li>
          The raw question text is never persisted. The audit trail stores a
          SHA-256 hash of the normalized query, the page route, the result
          kind (KB article, AI synthesis, or fallback), and the article IDs
          returned. No question content is recoverable from the audit.
        </li>
        <li>
          Help queries are scoped per tenant via row-level security. Queries
          authored by one tenant are never visible to another.
        </li>
        <li>
          Client-side guards strip obvious PHI patterns (medical record
          numbers, date-of-birth strings, long numeric identifiers) from the
          question before it leaves the user's browser. Users should not
          include resident names or other PHI in Help queries.
        </li>
      </ul>

      <h2>KB article authoring</h2>
      <p>
        KB articles are system content authored by WeConsult Rx staff. Bodies
        use placeholder names (Resident A, Facility X) and never contain
        actual PHI examples. Articles are versioned with a{" "}
        <code>last_updated</code> date and a difficulty rating. Each article
        carries a "Was this useful?" feedback control, which is stored per
        tenant and per user without including the question.
      </p>
    </LegalPage>
  );
}
